import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { getWindow } from 'ssr-window';
import { withLocalization } from '../shared/Context/LocalizationContext';
import { useLocalization } from '../custom-hooks/useLocalization';
import InsightCard from './cards/InsightCard';
import { device } from '../../utils/styled-component-mixins';
import ServerStylesConditionalWrapper from '../shared/ServerStylesConditionalWrapper';
import StaticCard from './cards/StaticCard';
import BdoSelectInputContainer from '../shared/BdoSelectInput/BdoSelectInputContainer';
import SkeletonCard from './cards/SkeletonCard';
import NoResultsCard from '../shared/NoResultsCard';

const CaseStudyLandingWrapperStyled = styled.div`
  .card-grid {
    display: grid;
    grid-template-columns: 100%;
    grid-template-rows: max-content;
    grid-gap: 1.6rem;
    grid-template-columns: repeat(auto-fill, minmax(35rem, 1fr));

    > .widget-spacing-wrapper {
      flex: 0 1 24%;
    }
  }

  .widget-spacing-wrapper {
    margin-bottom: 0;
  }

  .insight-card-wrapper,
  .insight-card {
    height: 100%;
  }

  .insight-card {
    display: flex;
    flex-direction: column;
    height: 100%;

    .animation-wrapper {
      align-items: flex-start;
      flex-direction: column !important;
      justify-content: space-between !important;
      display: flex;
      height: 100%;

      @media ${device.desktop} {
        align-items: flex-start;
        justify-content: flex-end;
      }

      .overflow-wrapper {
        align-self: flex-start;
      }
    }
  }

  .featured-card-content {
    flex: 1 1 auto;
    min-height: 22.4rem;

    @media ${device.desktop} {
      min-height: 31.2rem;
    }
  }

  .overflow-wrapper > a {
    text-decoration: none;
  }

  .cta-wrapper {
    display: flex;
    flex-direction: column-reverse;
    margin-top: 4rem;

    @media ${device.desktop} {
      flex-direction: column;
      margin-top: 0;
    }
  }

  .advanced-search-cta {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    margin-bottom: 8.3rem;

    @media ${device.desktop} {
      flex-direction: row;
      margin-bottom: 4rem;
      margin-top: 4rem;
    }

    a,
    p {
      font-size: 1.6rem;
      line-height: 2.4rem;
    }

    p {
      margin: 0;

      @media ${device.desktop} {
        margin-bottom: 1px;
        margin-right: 0.8rem;
      }
    }
  }

  .show-more-container {
    display: flex;
    justify-content: center;
    margin-bottom: 4rem;

    @media ${device.desktop} {
      margin: 4rem 0 8rem 0;
    }
  }
  .no-results-card {
    z-index: 1;
  }
`;

const CaseStudyLandingComponent = (props) => {
  const { resourceStrings } = useLocalization();
  const window = getWindow();
  const readMoreText = null;
  const filterLabel = resourceStrings['bdO.EventsWebinarsSliderWidget.FilterLabel'];

  const serviceLabel = resourceStrings['bdO.InsightLanding.FilterLabel.Service'];
  const industryLabel = resourceStrings['bdO.InsightLanding.FilterLabel.Industry'];
  const noResultsMessage = resourceStrings['bdO.InsightSectionWidget.NoResults'];

  const [selectedIndustry, setSelectedIndustry] = useState(props.selectedIndustry);
  const [selectedService, setSelectedService] = useState(props.selectedService);
  const [results, setResults] = useState(props.caseStudyCards);
  const [totalInsightCount, setTotalInsightCount] = useState(props?.insightCount ?? 0);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchAmount, setFetchAmount] = useState(window.innerWidth > 768 ? 20 : 8);
  const isFirstRender = useRef(true);

  const dropDownMap = (categories, firstItem) => [firstItem, ...Object.values(categories)];

  const serviceCategoryValues = dropDownMap(props.serviceCategories, serviceLabel);
  const industryCategoryValues = dropDownMap(props.industryCategories, industryLabel);

  const getSelectedOptionValue = (options, selectedOption) => options[selectedOption];

  const getIndexOfSelectedOption = (optionValues, categoryOptions, selectedOption) =>
    optionValues.indexOf(getSelectedOptionValue(categoryOptions, selectedOption));

  const indexOfPreselectedService = getIndexOfSelectedOption(
    serviceCategoryValues,
    props.serviceCategories,
    props.selectedService
  );
  const indexOfPreselectedIndustry = getIndexOfSelectedOption(
    industryCategoryValues,
    props.industryCategories,
    props.selectedIndustry
  );

  const getSelectedOptionKey = (options, selectedOption) => {
    const byIndex = options[selectedOption];
    const selectedKey = Object.keys(options).find(
      (key) => options[key] === selectedOption || options[key] === byIndex
    );

    return selectedKey;
  };

  const buildUrlWithParams = (currentResults, isApiUrl = true) => {
    const pathName = window.location.pathname;
    const engagementTypeParam = `insightType=casestudy&`;
    const serviceParam =
      selectedService && selectedService !== serviceLabel
        ? `insightService=${getSelectedOptionKey(props.serviceCategories, selectedService)}&`
        : '';
    const industryParam =
      selectedIndustry && selectedIndustry !== industryLabel
        ? `insightIndustry=${getSelectedOptionKey(props.industryCategories, selectedIndustry)}&`
        : '';
    const categoryParam = props.category?.length ? `category=${props.category}&` : '';

    // Only fetch 8 more when on the trending topics landing page.
    const fetchAmountParam = props?.isTrendingTopicLanding
      ? 'fetchAmount=8&'
      : `fetchAmount=${fetchAmount}&`;

    const staticCardsParentNodeId = `staticCardsParentNodeId=${props.staticCardsParentNodeId}`;
    const currentResultsParam = currentResults ? `&currentResults=${currentResults}` : '';

    if (isApiUrl) {
      return `/api/casestudies?${serviceParam}${industryParam}${categoryParam}${engagementTypeParam}${fetchAmountParam}${staticCardsParentNodeId}${currentResultsParam}`;
    }

    const url = `${pathName}?${serviceParam}${industryParam}${categoryParam}`;

    return url.trimEnd('&');
  };

  useEffect(() => {
    if (selectedService || selectedIndustry) {
      const updatedUrl = buildUrlWithParams(null, false);

      window.history.pushState(null, null, updatedUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndustry, selectedService]);

  const filters = [
    {
      filterOptions: serviceCategoryValues,
      onChange: setSelectedService,
      preselectedOption:
        props.selectedService && indexOfPreselectedService !== -1 ? indexOfPreselectedService : 0
    },
    {
      filterOptions: industryCategoryValues,
      onChange: setSelectedIndustry,
      preselectedOption:
        props.selectedIndustry && indexOfPreselectedIndustry !== -1 ? indexOfPreselectedIndustry : 0
    }
  ];

  const cardProps = {
    layout: 'standard',
    mediaSide: 'left'
  };

  const showMore = () => {
    const apiUrl = buildUrlWithParams(results?.length);
    setIsLoading(true);
    fetch(apiUrl)
      .then((response) => response.json())
      .then((data) => {
        setResults([...results, ...data.insights]);
        setTotalInsightCount(data.totalInsightCount);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const apiUrl = buildUrlWithParams();
    setIsLoading(true);
    fetch(apiUrl)
      .then((response) => response.json())
      .then((data) => {
        setResults(data.insights);
        setTotalInsightCount(data.totalInsightCount);
        setIsLoading(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndustry, selectedService]);

  useEffect(() => {
    setFetchAmount(window.innerWidth > 768 ? 20 : 8);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const hasMoreResults = results?.length < totalInsightCount;

  return (
    <ServerStylesConditionalWrapper>
      <CaseStudyLandingWrapperStyled>
        <BdoSelectInputContainer
          className="bdo-select-container"
          filterLabel={filterLabel}
          filters={filters}
        />
        <div className="card-grid-wrapper container-padding-md">
          <div className="card-grid">
            {isLoading
              ? Array.from(Array(fetchAmount), (_, index) => <SkeletonCard key={index} />)
              : results.map((item) =>
                  item.isStatic ? (
                    <StaticCard
                      key={item.nodeId}
                      cardProps={cardProps}
                      item={{
                        thumbnail1: item.image,
                        pageTitle: item.pageTitle,
                        url: item.url,
                        externalUrl: item.externalUrl,
                        ctaText: item.ctaText,
                        description: item.description,
                        isExternal: item.isExternal
                      }}
                    />
                  ) : (
                    <InsightCard
                      key={item.nodeId}
                      cardProps={cardProps}
                      item={{
                        thumbnail1: item.image,
                        primaryCategory: item.primaryCategory,
                        pageTitle: item.pageTitle,
                        publishDate: item.publishDate,
                        readMoreText: item.ctaText ? item.ctaText : readMoreText,
                        description: item.description,
                        url: item.url,
                        externalUrl: item.externalUrl,
                        isExternal: item.isExternal
                      }}
                    />
                  )
                )}
          </div>
          {results?.length === 0 && (
            <div>
              <NoResultsCard className="no-results-card" noResultsMessage={noResultsMessage} />
            </div>
          )}
          <div className="cta-wrapper">
            <div className="advanced-search-cta">
              {props.advancedSearchLinkText && props.advancedSearchLink && (
                <>
                  <p>{props.advancedSearchPrelinkText}</p>
                  <ButtonLink
                    text={props.advancedSearchLinkText}
                    link={props.advancedSearchLink}
                    target="_self"
                    type="tertiary"
                    theme
                  />
                </>
              )}
            </div>
            {hasMoreResults && !isLoading && (
              <div className="show-more-container">
                <ButtonLink
                  onClickFunction={showMore}
                  text="SHOW MORE"
                  type="secondary"
                  theme="light"
                  isButtonTag
                />
              </div>
            )}
          </div>
        </div>
      </CaseStudyLandingWrapperStyled>
    </ServerStylesConditionalWrapper>
  );
};

export default withLocalization(CaseStudyLandingComponent);
